import { useMemo } from 'react';
import type { PresenceMarker } from '../types';

/**
 * Index flat presence markers into a `rowKey -> (columnKey -> markers)` map so
 * each row and cell can look up only its own markers.
 *
 * Rebuilt only when the marker array identity changes, so presence updates
 * never touch presence-free rows. Keep `markers` referentially stable (a new
 * array only when presence actually changes) to preserve this.
 */
export function usePresenceIndex(
  markers: readonly PresenceMarker[] | undefined,
): ReadonlyMap<string, Map<string, PresenceMarker[]>> | null {
  return useMemo(() => {
    if (markers == null || markers.length === 0) return null;
    const byRow = new Map<string, Map<string, PresenceMarker[]>>();
    for (const marker of markers) {
      let row = byRow.get(marker.rowKey);
      if (row === undefined) {
        row = new Map();
        byRow.set(marker.rowKey, row);
      }
      const cell = row.get(marker.columnKey);
      if (cell === undefined) {
        row.set(marker.columnKey, [marker]);
      } else {
        cell.push(marker);
      }
    }
    return byRow;
  }, [markers]);
}
